import React from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import {
  Typography,
  useTheme
} from "@mui/material"

import { HomeRounded } from "@mui/icons-material"

import HKVLink from "../../HKVLink"

import { useLocation, matchPath } from "react-router-dom"

import { capitalizeFirstLetter } from "../../../lib/utils"

import { breadcrumbConfig } from "./config"

const Breadcrumbs = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const location = useLocation()
  const specialists = useSelector(state => state.specialists)

  // Find the most specific matching config entry.
  //
  // Sorting on the raw string length looked like a specificity measure but isn't:
  // "/observations/upload/:specialist/:upload_id" is a longer *string* than
  // "/observations/upload/:specialist/add_tank", so it won and the add-tank page ended its trail
  // with "Observation", having matched "add_tank" as if it were an observation id. What actually
  // decides specificity is how much of the path is literal: same number of segments, more fixed
  // ones, more specific.
  const specificity = (path) => {
    const segments = path.split("/").filter(Boolean)
    return {
      segments: segments.length,
      fixed: segments.filter(segment => !segment.startsWith(":")).length
    }
  }

  const matchedEntry = breadcrumbConfig
    .slice()
    .sort((a, b) => {
      const left = specificity(a.path)
      const right = specificity(b.path)
      return right.segments - left.segments || right.fixed - left.fixed
    })
    .find(entry => matchPath({ path: entry.path, end: true }, location.pathname))

  if (!matchedEntry) return null

  // Extract actual param values from the URL
  const match = matchPath({ path: matchedEntry.path, end: true }, location.pathname)
  const params = match?.params ?? {}

  // Resolve each crumb: replace :paramName with actual value, resolve toPattern if navigable
  const resolvedCrumbs = matchedEntry.crumbs.map((crumb, ix) => {
    // Resolve label: if it starts with ":", replace with actual param value.
    // The ":specialist" param is a role key (e.g. "scientist") whose display
    // name lives in specialists[role].title (e.g. "Researcher"), that can
    // differ from the capitalized role itself, so look it up instead of
    // guessing from the URL segment the way other dynamic params are handled.
    const paramName = crumb.label.slice(1)
    const rawValue = params[paramName]
    const label = crumb.label.startsWith(":")
      ? (paramName === "specialist" && specialists?.[rawValue]?.title)
        || capitalizeFirstLetter(rawValue?.replaceAll("-", " ").replaceAll("_", " ") ?? crumb.label)
      : crumb.label

    // Resolve the link target if navigable
    let to = null
    if (crumb.navigable && crumb.toPattern) {
      to = crumb.toPattern.replace(/:(\w+)/g, (_, key) => params[key] ?? key)
      if (crumb.keepSearch) to += location.search
    }

    return { label, to, navigable: crumb.navigable && !!to, isLast: ix === matchedEntry.crumbs.length - 1 }
  })

  return (
    <nav aria-label="breadcrumb" style={{ marginBottom: theme.spacing(2) }}>
      <ol style={{
        display: "flex", alignItems: "center", flexWrap: "wrap",
        gap: theme.spacing(0.5), listStyle: "none", margin: 0, padding: 0,
      }}>
        {/* Home */}
        <li style={{ display: "flex", alignItems: "center" }}>
          <HKVLink to="/" aria-label="Home" sx={{
            display: "flex", alignItems: "center",
            color: theme.palette.primary.main,
            borderBottom: `1px solid ${theme.palette.primary.main}`,
            "&:hover": {
              color: theme.palette.primary.dark,
              borderBottom: `1px solid ${theme.palette.primary.dark}`,
            }
          }}>
            <HomeRounded sx={{ fontSize: 18 }} />
          </HKVLink>
        </li>

        {resolvedCrumbs.map((crumb, ix) => (
          <React.Fragment key={ix}>
            {/* Separator */}
            <li aria-hidden="true" style={{ color: theme.palette.text.secondary, userSelect: "none" }}>
              <Typography variant="body2" component="span" sx={{ color: "inherit", opacity: 0.5 }}>/</Typography>
            </li>

            {/* Crumb */}
            <li>
              {crumb.navigable ? (
                <HKVLink to={crumb.to} sx={{
                  color: theme.palette.primary.main,
                  borderBottom: `1px solid ${theme.palette.primary.main}`,
                  "&:hover": {
                    color: theme.palette.primary.dark,
                    borderBottom: `1px solid ${theme.palette.primary.dark}`
                  }
                }}>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      lineHeight: 1.125,
                      color: "inherit"
                    }}>
                    {t(crumb.label)}
                  </Typography>
                </HKVLink>
              ) : (
                <Typography
                  component="span"
                  variant="body2"
                  aria-current={crumb.isLast ? "page" : undefined}
                  sx={{
                    lineHeight: 1.25,
                    color: crumb.isLast ? theme.palette.text.primary : theme.palette.text.secondary,
                    fontWeight: crumb.isLast ? 500 : 400
                  }}>
                  {t(crumb.label)}
                </Typography>
              )}
            </li>
          </React.Fragment>
        ))}
      </ol>
    </nav>
  );
}

export default Breadcrumbs